import { useState, useEffect, useMemo } from 'react';
import EntitlementsService from '../services/EntitlementsService';
import { getTier2AttributesFromResponse, type EntityEntitlementForSchemaResponse } from '../types/EntityEntitlements';
import { createEntityRef, getEntityValueFromARef } from '../types';
import type { ReferenceAttribute } from './useReferenceAttributeLoader';

export interface UseEntityUpsertTier2Params {
    open: boolean;
    entityType: string | undefined;
    /** Used to initialize Ent tab tier2 type selector when dialog opens. */
    propEntityType?: string;
    formData: Record<string, unknown>;
    isRequestApprovalType: boolean;
    isPluginFunction: boolean;
}

function mapTier2ReferenceAttributes(
    entitlement: EntityEntitlementForSchemaResponse | null,
    options?: { includeCanIntersect?: boolean }
): ReferenceAttribute[] {
    const attrs = getTier2AttributesFromResponse(entitlement);
    return attrs
        .filter((a: { IsReference?: boolean; CanIntersect?: boolean }) =>
            options?.includeCanIntersect ? a.IsReference === true || a.CanIntersect === true : a.IsReference === true
        )
        .map((a: Record<string, unknown>) => {
            const id = (a.Id ?? a.DisplayName ?? '').toString().trim();
            const name = id;
            const displayId = id;
            return {
                name: name || displayId,
                id: displayId || name,
                value: displayId || name,
                label: (a.DisplayName ?? name ?? displayId).toString(),
                type: (a.Type ?? 'string').toString(),
                required: false,
                multiValued: false,
                isReference: !!a.IsReference,
                ...(options?.includeCanIntersect ? { canIntersect: !!a.CanIntersect } : {}),
                entities: ((a.Entities as unknown[]) ?? [])
                    .map((e: unknown) => {
                        const ent = e as Record<string, unknown>;
                        const v = ent?.DisplayName ?? ent?.Id ?? e;
                        return v != null ? String(v) : '';
                    })
                    .filter((s) => s.length > 0),
                isReferenceTo: Array.isArray(a.IsReferenceTo) ? a.IsReferenceTo : [],
            };
        })
        .filter((a: { name: string; id: string }) => a.name || a.id);
}

/** Tier2 entitlement fetches + Ent tab resolution for GenericEntityUpsertDialog. */
export function useEntityUpsertTier2({
    open,
    entityType,
    propEntityType,
    formData,
    isRequestApprovalType,
    isPluginFunction,
}: UseEntityUpsertTier2Params) {
    const [tier2Entitlement, setTier2Entitlement] = useState<EntityEntitlementForSchemaResponse | null>(null);
    const [tier2PossessiveScopeEntitlement, setTier2PossessiveScopeEntitlement] =
        useState<EntityEntitlementForSchemaResponse | null>(null);
    const [tier2PossessiveTargetAttributeEntitlement, setTier2PossessiveTargetAttributeEntitlement] =
        useState<EntityEntitlementForSchemaResponse | null>(null);
    const [loadingPossessiveScopeTier2, setLoadingPossessiveScopeTier2] = useState(false);
    const [loadingPossessiveTargetAttributeTier2, setLoadingPossessiveTargetAttributeTier2] = useState(false);
    const [tier2EntitlementError, setTier2EntitlementError] = useState<string | null>(null);
    const [tier2PossessiveScopeError, setTier2PossessiveScopeError] = useState<string | null>(null);
    const [tier2PossessiveTargetError, setTier2PossessiveTargetError] = useState<string | null>(null);
    const [entTier2SelectedEntityType, setEntTier2SelectedEntityType] = useState('');

    useEffect(() => {
        if (!open) {
            setTier2EntitlementError(null);
            setTier2PossessiveScopeError(null);
            setTier2PossessiveTargetError(null);
            return;
        }
        setEntTier2SelectedEntityType((propEntityType ?? entityType ?? '').trim().toLowerCase() || '');
    }, [open, propEntityType, entityType]);

    const possessiveScope = (getEntityValueFromARef('PossessiveScope', formData) ?? '').toString().trim() || '';

    useEffect(() => {
        if (!open || !entityType?.trim()) {
            setTier2Entitlement(null);
            return;
        }
        let cancelled = false;
        const schemaEntityId = entityType.trim().toLowerCase();
        EntitlementsService.fetchEntityEntitlementForSchemaEntity(schemaEntityId)
            .then((data) => {
                if (!cancelled) {
                    if (data) setTier2Entitlement(data);
                    else setTier2Entitlement(null);
                    setTier2EntitlementError(null);
                }
            })
            .catch((err) => {
                console.error(`Selector entitlement (tier2) failed for entityType "${schemaEntityId}":`, err);
                if (!cancelled) {
                    setTier2Entitlement(null);
                    setTier2EntitlementError(err instanceof Error ? err.message : String(err));
                }
            });
        return () => {
            cancelled = true;
        };
    }, [open, entityType]);

    useEffect(() => {
        if (!open || (!isRequestApprovalType && !isPluginFunction)) {
            setTier2PossessiveScopeEntitlement(null);
            setLoadingPossessiveScopeTier2(false);
            return;
        }
        if (!possessiveScope) {
            setTier2PossessiveScopeEntitlement(null);
            setLoadingPossessiveScopeTier2(false);
            return;
        }
        let cancelled = false;
        setLoadingPossessiveScopeTier2(true);
        setTier2PossessiveScopeError(null);
        const schemaEntityId = possessiveScope.toLowerCase();
        EntitlementsService.fetchEntityEntitlementForSchemaEntity(schemaEntityId)
            .then((data) => {
                if (!cancelled && data) {
                    setTier2PossessiveScopeEntitlement(data);
                    setTier2PossessiveScopeError(null);
                } else if (!cancelled) setTier2PossessiveScopeEntitlement(null);
            })
            .catch((err) => {
                console.error(`Selector entitlement (tier2 PossessiveScope) failed for entityType "${schemaEntityId}":`, err);
                if (!cancelled) {
                    setTier2PossessiveScopeEntitlement(null);
                    setTier2PossessiveScopeError(err instanceof Error ? err.message : String(err));
                }
            })
            .finally(() => {
                if (!cancelled) setLoadingPossessiveScopeTier2(false);
            });
        return () => {
            cancelled = true;
        };
    }, [open, isRequestApprovalType, isPluginFunction, possessiveScope]);

    const rawPossessiveTarget = getEntityValueFromARef('PossessiveTargetAttribute', formData) ?? '';
    const possessiveTargetAttributeValue = (() => {
        if (rawPossessiveTarget == null || rawPossessiveTarget === '') return '';
        if (typeof rawPossessiveTarget === 'string') return rawPossessiveTarget.trim();
        if (typeof rawPossessiveTarget === 'object') {
            const id =
                (rawPossessiveTarget as Record<string, unknown>).id ??
                (rawPossessiveTarget as Record<string, unknown>).Id ??
                (rawPossessiveTarget as Record<string, unknown>).value ??
                (rawPossessiveTarget as Record<string, unknown>).name ??
                (rawPossessiveTarget as Record<string, unknown>).Name ??
                '';
            return id && typeof id === 'string' ? id.trim() : '';
        }
        return String(rawPossessiveTarget).trim();
    })();

    const possessiveTargetAttributeKey = possessiveTargetAttributeValue.trim()
        ? createEntityRef(possessiveTargetAttributeValue.trim()).displayName.trim().toLowerCase()
        : '';

    const targetEntityTypeForPossessiveTarget = useMemo(() => {
        const attrs = getTier2AttributesFromResponse(tier2PossessiveScopeEntitlement);
        if (!possessiveTargetAttributeKey || attrs.length === 0) return null;
        const selectedAttr = attrs.find((a: { Id?: string; DisplayName?: string }) => {
            const raw = (a.Id ?? a.DisplayName ?? '').toString().trim();
            const id = raw ? createEntityRef(raw).displayName.trim().toLowerCase() : '';
            return id === possessiveTargetAttributeKey;
        });
        if (!selectedAttr) return null;
        const rawEntities = (selectedAttr as { Entities?: unknown }).Entities;
        const entities = Array.isArray(rawEntities) ? rawEntities : [];
        const firstEntity = entities[0];
        if (firstEntity != null) {
            const id =
                typeof firstEntity === 'object' && firstEntity !== null
                    ? ((firstEntity as Record<string, unknown>).Id ??
                          (firstEntity as Record<string, unknown>).DisplayName ??
                          (firstEntity as Record<string, unknown>).id ??
                          (firstEntity as Record<string, unknown>).displayName)
                    : firstEntity;
            if (id != null && typeof id === 'string') {
                const normalized = id.trim().toLowerCase();
                if (normalized) return normalized;
            }
        }
        const refTo = (selectedAttr as { IsReferenceTo?: unknown }).IsReferenceTo;
        if (Array.isArray(refTo) && refTo.length > 0) {
            const first = refTo[0];
            if (typeof first === 'string') {
                const normalized = first.trim().toLowerCase();
                if (normalized) return normalized;
            }
        }
        if ((selectedAttr as { CanIntersect?: boolean }).CanIntersect === true && possessiveScope) {
            return possessiveScope.toLowerCase();
        }
        return null;
    }, [possessiveTargetAttributeKey, tier2PossessiveScopeEntitlement, possessiveScope]);

    const possessiveTargetCanIntersect = useMemo(() => {
        const attrs = getTier2AttributesFromResponse(tier2PossessiveScopeEntitlement);
        if (!possessiveTargetAttributeKey || attrs.length === 0) return false;
        const selectedAttr = attrs.find((a: { Id?: string; DisplayName?: string }) => {
            const raw = (a.Id ?? a.DisplayName ?? '').toString().trim();
            const id = raw ? createEntityRef(raw).displayName.trim().toLowerCase() : '';
            return id === possessiveTargetAttributeKey;
        });
        return !!(selectedAttr as { CanIntersect?: boolean })?.CanIntersect;
    }, [possessiveTargetAttributeKey, tier2PossessiveScopeEntitlement]);

    useEffect(() => {
        if (!open || (!isRequestApprovalType && !isPluginFunction)) {
            setTier2PossessiveTargetAttributeEntitlement(null);
            setLoadingPossessiveTargetAttributeTier2(false);
            return;
        }
        if (!targetEntityTypeForPossessiveTarget) {
            setTier2PossessiveTargetAttributeEntitlement(null);
            setLoadingPossessiveTargetAttributeTier2(false);
            return;
        }
        let cancelled = false;
        setLoadingPossessiveTargetAttributeTier2(true);
        setTier2PossessiveTargetError(null);
        EntitlementsService.fetchEntityEntitlementForSchemaEntity(targetEntityTypeForPossessiveTarget)
            .then((data) => {
                if (!cancelled && data) {
                    setTier2PossessiveTargetAttributeEntitlement(data);
                    setTier2PossessiveTargetError(null);
                } else if (!cancelled) setTier2PossessiveTargetAttributeEntitlement(null);
            })
            .catch((err) => {
                console.error(
                    `Selector entitlement (tier2 PossessiveTargetAttribute target type) failed for entityType "${targetEntityTypeForPossessiveTarget}":`,
                    err
                );
                if (!cancelled) {
                    setTier2PossessiveTargetAttributeEntitlement(null);
                    setTier2PossessiveTargetError(err instanceof Error ? err.message : String(err));
                }
            })
            .finally(() => {
                if (!cancelled) setLoadingPossessiveTargetAttributeTier2(false);
            });
        return () => {
            cancelled = true;
        };
    }, [open, isRequestApprovalType, isPluginFunction, targetEntityTypeForPossessiveTarget]);

    const referenceAttributesFromTier2 = useMemo(
        () => mapTier2ReferenceAttributes(tier2Entitlement),
        [tier2Entitlement]
    );

    const referenceAttributesFromPossessiveScope = useMemo(
        () => mapTier2ReferenceAttributes(tier2PossessiveScopeEntitlement, { includeCanIntersect: true }),
        [tier2PossessiveScopeEntitlement]
    );

    const referenceAttributesFromPossessiveTargetAttribute = useMemo(() => {
        const refAttrs = mapTier2ReferenceAttributes(tier2PossessiveTargetAttributeEntitlement);
        if (
            possessiveTargetCanIntersect &&
            !refAttrs.some((a) => a.id.toLowerCase() === '$.id' || a.id.toLowerCase() === 'id')
        ) {
            refAttrs.unshift({
                name: '$.Id',
                id: '$.Id',
                value: '$.Id',
                label: 'Id',
                type: 'string',
                required: false,
                multiValued: false,
                isReference: false,
                entities: [],
                isReferenceTo: [],
            });
        }
        return refAttrs;
    }, [tier2PossessiveTargetAttributeEntitlement, possessiveTargetCanIntersect]);

    const attributesFromPossessiveTargetAttributeTier2 = useMemo(() => {
        const attrs = getTier2AttributesFromResponse(tier2PossessiveTargetAttributeEntitlement);
        const refAttrs = attrs
            .filter((a: { IsReference?: boolean }) => a.IsReference === true)
            .map((a: Record<string, unknown>) => {
                const id = (a.Id ?? a.id ?? a.DisplayName ?? a.displayName ?? '').toString().trim();
                const displayId = id;
                const label = (a.DisplayName ?? a.displayName ?? id).toString();
                return { name: displayId || id, label };
            })
            .filter((a: { name: string }) => a.name);
        if (
            possessiveTargetCanIntersect &&
            !refAttrs.some((a) => a.name.toLowerCase() === '$.id' || a.name.toLowerCase() === 'id')
        ) {
            refAttrs.unshift({ name: '$.Id', label: 'Id' });
        }
        return refAttrs;
    }, [tier2PossessiveTargetAttributeEntitlement, possessiveTargetCanIntersect]);

    const currentEntityTypeKey = entityType?.trim().toLowerCase() || '';

    const entTier2TypesInPlay = useMemo(() => {
        const list = currentEntityTypeKey ? [currentEntityTypeKey] : [];
        if ((isRequestApprovalType || isPluginFunction) && possessiveScope) {
            const scopeKey = possessiveScope.toLowerCase();
            if (scopeKey && !list.includes(scopeKey)) list.push(scopeKey);
        }
        if ((isRequestApprovalType || isPluginFunction) && targetEntityTypeForPossessiveTarget) {
            if (!list.includes(targetEntityTypeForPossessiveTarget)) list.push(targetEntityTypeForPossessiveTarget);
        }
        return list;
    }, [currentEntityTypeKey, isRequestApprovalType, isPluginFunction, possessiveScope, targetEntityTypeForPossessiveTarget]);

    useEffect(() => {
        if (!open) return;
        const current = entTier2SelectedEntityType?.toLowerCase() || '';
        if (!entTier2TypesInPlay.length) return;
        if (current && entTier2TypesInPlay.includes(current)) return;
        setEntTier2SelectedEntityType(currentEntityTypeKey);
    }, [open, currentEntityTypeKey, entTier2TypesInPlay, entTier2SelectedEntityType]);

    const entTier2Resolved = useMemo(() => {
        const selectedKey = (entTier2SelectedEntityType || currentEntityTypeKey).toLowerCase();
        const isCurrentType = selectedKey === currentEntityTypeKey;
        const isPossessiveScopeType = selectedKey === possessiveScope.toLowerCase();
        const isPossessiveTargetType =
            !!targetEntityTypeForPossessiveTarget && selectedKey === targetEntityTypeForPossessiveTarget;
        const data = isCurrentType
            ? tier2Entitlement
            : isPossessiveScopeType
              ? tier2PossessiveScopeEntitlement
              : isPossessiveTargetType
                ? tier2PossessiveTargetAttributeEntitlement
                : null;
        const loading = isCurrentType
            ? tier2Entitlement == null
            : isPossessiveScopeType
              ? loadingPossessiveScopeTier2
              : isPossessiveTargetType
                ? loadingPossessiveTargetAttributeTier2
                : true;
        const error = isCurrentType
            ? tier2EntitlementError
            : isPossessiveScopeType
              ? tier2PossessiveScopeError
              : isPossessiveTargetType
                ? tier2PossessiveTargetError
                : null;
        return { data, loading, error };
    }, [
        entTier2SelectedEntityType,
        currentEntityTypeKey,
        possessiveScope,
        targetEntityTypeForPossessiveTarget,
        tier2Entitlement,
        tier2PossessiveScopeEntitlement,
        tier2PossessiveTargetAttributeEntitlement,
        loadingPossessiveScopeTier2,
        loadingPossessiveTargetAttributeTier2,
        tier2EntitlementError,
        tier2PossessiveScopeError,
        tier2PossessiveTargetError,
    ]);

    return {
        tier2Entitlement,
        tier2PossessiveScopeEntitlement,
        tier2PossessiveTargetAttributeEntitlement,
        loadingPossessiveScopeTier2,
        loadingPossessiveTargetAttributeTier2,
        tier2EntitlementError,
        tier2PossessiveScopeError,
        tier2PossessiveTargetError,
        possessiveScope,
        possessiveTargetAttributeKey,
        targetEntityTypeForPossessiveTarget,
        possessiveTargetCanIntersect,
        referenceAttributesFromTier2,
        referenceAttributesFromPossessiveScope,
        referenceAttributesFromPossessiveTargetAttribute,
        attributesFromPossessiveTargetAttributeTier2,
        entTier2SelectedEntityType,
        setEntTier2SelectedEntityType,
        entTier2TypesInPlay,
        entTier2Resolved,
        currentEntityTypeKey,
    };
}
